import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import ButtonWithIcon from 'chameleon/packages/blue-book-player/library/button-with-icon';
import Button from 'chameleon/packages/blue-book-player/library/button';
import {
  doSetHiddenAnswer,
  setCrossedOutA11y,
  clearCrossedOutA11y,
  isInputElement,
} from 'chameleon/packages/blue-book-player/utils';
import { SELECTORS } from 'chameleon/packages/blue-book-player/constants/selectors';

const CrossOutQuestionToggle = (props) => {
  const [isUserAction, setIsUserAction] = useState(false);
  const buttonRef = useRef();
  const [isCrossedOut, setIsCrossedOut] = useState(null);
  const {
    optionContainer,
    crossOutReducer,
    index,
    learnosityQuestionApi,
    learnosityQuestion,
    isStudentAccount,
  } = props;
  const [crossOutState, dispatchers] = crossOutReducer;
  const reference = learnosityQuestion.metadata.sheet_reference;

  const focusButton = () => {
    buttonRef?.current?.focus();
  };

  const crossOutOption = () => {
    const optionValue = optionContainer.querySelector(
      SELECTORS.OPTION_ANSWER_INPUT,
    ).value;
    const metadata = learnosityQuestionApi.response.getMetadata();
    if (metadata?.maskedOptions?.[optionValue]) {
      optionContainer.dataset.masked = 'true';
      setIsCrossedOut(true);
    }
  };

  useEffect(() => {
    //@todo - revisit this, it needs improvements - it is also called when clicking UNDO
    //If the main toggle was turned off, with crossed-out options we need to cross-out them
    //when the main toggle is turned back on
    if (
      crossOutState?.isEnabled &&
      optionContainer &&
      !isCrossedOut &&
      crossOutState?.init
    ) {
      crossOutOption();
    }
  }, [
    crossOutState?.isEnabled,
    isCrossedOut,
    optionContainer,
    crossOutState?.init,
  ]);

  useEffect(() => {
    if (crossOutState?.isEnabled && isCrossedOut !== null) {
      dispatchers.setCrossedOutByRefCount(reference, isCrossedOut);
    }
  }, [isCrossedOut, crossOutState?.init, crossOutState?.isEnabled, reference]);

  useEffect(() => {
    const onOptionClick = (e) => {
      if (!isInputElement(e.target) && isStudentAccount) return;

      const answerOptionEl = e.target.closest(SELECTORS.OPTION_ANSWER);

      if (answerOptionEl?.dataset?.masked === 'true') {
        toggleCrossOut(answerOptionEl);
      }
    };
    if (optionContainer) {
      optionContainer.addEventListener('click', onOptionClick);
    }

    return () => {
      if (optionContainer) {
        optionContainer.removeEventListener('click', onOptionClick);
      }
    };
  }, [optionContainer, isCrossedOut]);

  useEffect(() => {
    if (optionContainer) {
      const optionLabel = optionContainer.querySelector(
        SELECTORS.OPTION_ANSWER_LABEL,
      );
      const optionInput = optionContainer.querySelector(
        SELECTORS.OPTION_ANSWER_INPUT,
      );
      const elementsAvailable = optionLabel && optionInput;
      if (elementsAvailable && isCrossedOut) {
        optionLabel.setAttribute('aria-label', `crossed-out option ${index}`);
        setCrossedOutA11y(optionInput, optionLabel);
      } else if (elementsAvailable && !isCrossedOut) {
        optionLabel.removeAttribute('aria-label');
        clearCrossedOutA11y(optionInput, optionLabel);
      }
    }
  }, [optionContainer, isCrossedOut, index]);

  const clearIfAnsweredOption = (answeredOptionEl) => {
    const isSelected = answeredOptionEl.classList.contains('lrn_selected');
    if (isSelected && learnosityQuestion?.multiple_responses) {
      answeredOptionEl.querySelector(SELECTORS.OPTION_ANSWER_INPUT).click();
    } else if (isSelected) {
      learnosityQuestionApi.resetResponse();
    }
  };

  const toggleCrossOut = (answerOptionEl) => {
    optionContainer.dataset.masked = (!isCrossedOut).toString();
    const canStoreResponseMetadata =
      learnosityQuestionApi?.isAttempted() ||
      learnosityQuestionApi.isResponseReset();
    const answerOptionInputEl = answerOptionEl.querySelector(
      SELECTORS.OPTION_ANSWER_INPUT,
    );
    if (canStoreResponseMetadata) {
      saveToggleMaskResponse({
        [answerOptionInputEl.value]: !isCrossedOut,
      });
      clearIfAnsweredOption(answerOptionEl);
    } else {
      //Without having the question answered setMetadata() fails
      //Click the crossed-out answer option to have an answer selected
      const undo = doSetHiddenAnswer(answerOptionInputEl);
      //Allows the click event to take place, allow learnosity to config the answered question
      setTimeout(() => {
        //We reset the answer as it is not a user action
        learnosityQuestionApi.resetResponse();
        undo();
        saveToggleMaskResponse({
          [answerOptionInputEl.value]: !isCrossedOut,
        });
      }, 0);
    }
    setIsCrossedOut(!isCrossedOut);
  };

  const hasMultipleResponses = learnosityQuestion?.multiple_responses;

  const saveToggleMaskResponse = (responseStatus = {}) => {
    if (isStudentAccount) {
      const { getMetadata, setMetadata } = learnosityQuestionApi.response;
      const metadata = getMetadata();
      const updatedMetadata = {
        ...(metadata || {}),
        maskedOptions: {
          ...(metadata?.maskedOptions || {}),
          ...responseStatus,
        },
      };
      setMetadata(updatedMetadata);
    }
  };

  const optionIndex = index.toUpperCase();
  const accessibility = {
    'aria-pressed': isCrossedOut,
    'aria-label': `Cross-out option ${optionIndex}`,
  };

  const iconAccessibility = {
    'aria-hidden': true,
  };

  const onButtonClick = () => {
    toggleCrossOut(optionContainer);
    setIsUserAction(true);
  };

  useEffect(() => {
    if (isUserAction) {
      focusButton();
      setIsUserAction(false);
    }
  }, [isCrossedOut, isUserAction]);

  return (
    <div
      className={cn('absolute top-0 h-full pt-[15px]', {
        hidden: !crossOutState.isEnabled,
        flex: crossOutState.isEnabled,
        '-right-[42px]': !isCrossedOut && hasMultipleResponses,
        '-right-[50px]': isCrossedOut || !hasMultipleResponses,
      })}
    >
      {isCrossedOut ? (
        <Button
          ref={buttonRef}
          isBlueBookLink
          size="custom"
          className="m-auto"
          onClick={onButtonClick}
          accessibility={{
            'aria-label': `Undo option-${optionIndex} cross-out`,
          }}
        >
          Undo
        </Button>
      ) : null}
      <div className="m-auto">
        {!isCrossedOut && hasMultipleResponses ? (
          <ButtonWithIcon
            ref={buttonRef}
            size="custom"
            isBlueBookIcon
            isIcon
            icon="mcqCheckoutCrossout"
            onClick={onButtonClick}
            data-cy="crossout-question-toggle"
            className="m-auto relative"
            iconWidth="28"
            iconHeight="26"
            accessibility={accessibility}
            iconAccessibility={iconAccessibility}
          />
        ) : null}
        {!isCrossedOut && !hasMultipleResponses ? (
          <ButtonWithIcon
            ref={buttonRef}
            size="custom"
            isBlueBookIcon
            isIcon
            icon="mcqRadioCrossout"
            onClick={onButtonClick}
            data-cy="crossout-question-toggle"
            className="m-auto relative"
            iconWidth="26"
            iconHeight="20"
            accessibility={accessibility}
            iconAccessibility={iconAccessibility}
          >
            <div className="absolute uppercase text-12 font-bold top-0 left-[9px]">
              {index}
            </div>
          </ButtonWithIcon>
        ) : null}
      </div>
    </div>
  );
};

export default CrossOutQuestionToggle;
